/**
 * Attendance — /attendance (instructor, tenant admin)
 *
 * Pick a batch and a date, mark the roster, save. Re-opening the same day shows what was
 * marked and corrects it in place rather than taking a second register.
 *
 * The Report tab is the same data read sideways: every student in the batch with their
 * percentage over a period.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import { StatTiles } from '../../components/shell/Page';
import { useTabLabel } from '../../components/shell/TabsContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  CalendarCheck, CaretLeft, CaretRight, FloppyDisk, Users, CheckCircle, XCircle,
  Clock, Prohibit, ChartBar, Trash,
} from '@phosphor-icons/react';

const today = () => new Date().toISOString().slice(0, 10);
const shiftDate = (iso, days) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const prettyDate = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};

/** The four marks, in the order a teacher reaches for them. */
export const STATUS_META = {
  present: { label: 'Present', icon: CheckCircle, on: 'bg-emerald-600 text-white border-emerald-600', off: 'text-emerald-700 hover:bg-emerald-50 border-border' },
  absent: { label: 'Absent', icon: XCircle, on: 'bg-red-600 text-white border-red-600', off: 'text-red-700 hover:bg-red-50 border-border' },
  late: { label: 'Late', icon: Clock, on: 'bg-amber-500 text-white border-amber-500', off: 'text-amber-700 hover:bg-amber-50 border-border' },
  excused: { label: 'Excused', icon: Prohibit, on: 'bg-slate-500 text-white border-slate-500', off: 'text-slate-600 hover:bg-slate-50 border-border' },
};

export const rateTone = (value) => {
  if (value === null || value === undefined) return 'text-muted-foreground';
  if (value >= 85) return 'text-emerald-700';
  if (value >= 70) return 'text-amber-700';
  return 'text-red-700';
};

export default function Attendance() {
  const { api } = useAuth();
  const [params, setParams] = useSearchParams();

  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState(params.get('batch') || '');
  const [date, setDate] = useState(params.get('date') || today());
  const [session, setSession] = useState(null);
  const [marks, setMarks] = useState({});        // student id → status
  const [topic, setTopic] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [report, setReport] = useState(null);
  const [tab, setTab] = useState('mark');

  useTabLabel('Attendance');

  useEffect(() => {
    api.get('/attendance/batches')
      .then((r) => {
        setBatches(r.data || []);
        setBatchId((cur) => cur || r.data?.[0]?.id || '');
      })
      .catch(() => setBatches([]));
  }, [api]);

  const load = useCallback(async () => {
    if (!batchId) { setLoading(false); setSession(null); return; }
    setLoading(true);
    try {
      const r = await api.get(`/attendance/session?batch_id=${batchId}&date=${date}`);
      setSession(r.data);
      setTopic(r.data.session?.topic || '');
      const next = {};
      (r.data.students || []).forEach((s) => { if (s.mark?.status) next[s.id] = s.mark.status; });
      setMarks(next);
      setDirty(false);
    } catch (e) {
      setSession(null);
      toast.error(e.response?.data?.detail || 'Could not open the register');
    } finally {
      setLoading(false);
    }
  }, [api, batchId, date]);
  useEffect(() => { load(); }, [load]);

  const loadReport = useCallback(async () => {
    if (!batchId) return;
    try { setReport((await api.get(`/attendance/report?batch_id=${batchId}`)).data); }
    catch { setReport(null); }
  }, [api, batchId]);
  useEffect(() => { if (tab === 'report') loadReport(); }, [tab, loadReport]);

  useEffect(() => {
    const next = {};
    if (batchId) next.batch = batchId;
    if (date) next.date = date;
    setParams(next, { replace: true });
  }, [batchId, date, setParams]);

  const students = session?.students || [];
  const setMark = (studentId, status) => {
    setMarks((m) => ({ ...m, [studentId]: m[studentId] === status ? undefined : status }));
    setDirty(true);
  };
  const markAll = (status) => {
    const next = {};
    students.forEach((s) => { next[s.id] = status; });
    setMarks(next);
    setDirty(true);
  };

  const counts = useMemo(() => {
    const c = { present: 0, absent: 0, late: 0, excused: 0, unmarked: 0 };
    students.forEach((s) => { const v = marks[s.id]; if (v) c[v] += 1; else c.unmarked += 1; });
    return c;
  }, [students, marks]);

  const counted = counts.present + counts.absent + counts.late;
  const liveRate = counted ? Math.round(((counts.present + counts.late) / counted) * 1000) / 10 : null;

  const save = async () => {
    const payload = students
      .filter((s) => marks[s.id])
      .map((s) => ({ student_id: s.id, status: marks[s.id] }));
    if (!payload.length) { toast.error('Mark at least one student.'); return; }
    setSaving(true);
    try {
      await api.put('/attendance/session', { batch_id: batchId, date, topic, marks: payload });
      toast.success(`Attendance saved for ${prettyDate(date)}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the register');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm(`Delete the register for ${prettyDate(date)}? This cannot be undone.`)) return;
    try {
      await api.delete(`/attendance/session?batch_id=${batchId}&date=${date}`);
      toast.success('Register cleared');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not clear it');
    }
  };

  const batch = batches.find((b) => b.id === batchId);

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="attendance-page"
        title="Attendance"
        subtitle={batch ? `${batch.name} · ${prettyDate(date)}` : 'Pick a batch to mark'}
        onBack={null}
        onRefresh={load}
        refreshing={loading}
        loading={loading && !session}
        dirty={dirty}
        primary={tab === 'mark' && students.length ? [
          { key: 'save', label: saving ? 'Saving…' : 'Save attendance', icon: FloppyDisk, onClick: save, disabled: saving, testid: 'save-attendance' },
        ] : []}
        secondary={tab === 'mark' && session?.marked ? [
          { key: 'clear', label: 'Clear this day', icon: Trash, variant: 'outline', onClick: clear, testid: 'clear-attendance' },
        ] : []}
      >
        <FormSection title="Register for" icon={CalendarCheck} bodyClassName="p-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="field">
              <Label htmlFor="a-batch">Batch</Label>
              <select id="a-batch" className="form-select w-[260px]" value={batchId}
                onChange={(e) => setBatchId(e.target.value)} data-testid="attendance-batch">
                {batches.length === 0 && <option value="">No batches</option>}
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}{b.code ? ` (${b.code})` : ''} · {b.student_count}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <Label htmlFor="a-date">Date</Label>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setDate(shiftDate(date, -1))} data-testid="prev-day"><CaretLeft className="w-4 h-4" /></Button>
                <Input id="a-date" type="date" value={date} onChange={(e) => setDate(e.target.value || today())} className="w-[150px]" data-testid="attendance-date" />
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setDate(shiftDate(date, 1))} data-testid="next-day"><CaretRight className="w-4 h-4" /></Button>
                {date !== today() && <Button variant="ghost" size="sm" className="h-8" onClick={() => setDate(today())}>Today</Button>}
              </div>
            </div>
            <div className="field">
              <Label htmlFor="a-topic">What was covered (optional)</Label>
              <Input id="a-topic" value={topic} onChange={(e) => { setTopic(e.target.value); setDirty(true); }}
                placeholder="e.g. Fractions revision" className="w-[260px]" data-testid="attendance-topic" />
            </div>
          </div>
        </FormSection>

        <Tabs value={tab} onValueChange={setTab} className="space-y-3">
          <TabsList>
            <TabsTrigger value="mark" className="flex items-center gap-2"><Users className="w-4 h-4" />Mark the roster</TabsTrigger>
            <TabsTrigger value="report" className="flex items-center gap-2"><ChartBar className="w-4 h-4" />Report</TabsTrigger>
          </TabsList>

          {/* ------------------------------------------------------------- marking */}
          <TabsContent value="mark" className="space-y-3">
            <StatTiles items={[
              { label: 'On the roster', value: students.length, icon: Users, testid: 'tile-roster' },
              { label: 'Present', value: counts.present + counts.late, icon: CheckCircle, iconClass: 'bg-emerald-100 text-emerald-700', testid: 'tile-present' },
              { label: 'Absent', value: counts.absent, icon: XCircle, iconClass: counts.absent ? 'bg-red-100 text-red-700' : undefined, testid: 'tile-absent' },
              { label: 'Attendance', value: liveRate === null ? '—' : `${liveRate}%`, icon: ChartBar, iconClass: 'bg-sky-100 text-sky-700', testid: 'tile-rate' },
            ]} />

            <FormSection
              title={session?.marked ? 'Marked — change anything and save again' : 'Not marked yet'}
              icon={Users}
              actions={students.length ? (
                <div className="flex gap-1.5">
                  <Button variant="outline" size="sm" className="h-7 text-[12px]" onClick={() => markAll('present')} data-testid="all-present">All present</Button>
                  <Button variant="ghost" size="sm" className="h-7 text-[12px]" onClick={() => { setMarks({}); setDirty(true); }} data-testid="clear-marks">Clear</Button>
                </div>
              ) : null}
              bodyClassName="p-0"
            >
              {students.length === 0 ? (
                <p className="p-4 text-[13px] text-muted-foreground" data-testid="no-students">
                  {batchId ? 'This batch has no students yet — add them in Batches first.' : 'Pick a batch above.'}
                </p>
              ) : (
                <table className="w-full text-[13px]" data-testid="attendance-roster">
                  <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium px-3 py-2 w-[120px]">ID</th>
                      <th className="text-left font-medium px-3 py-2">Student</th>
                      <th className="text-right font-medium px-3 py-2">Mark</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {students.map((s) => (
                      <tr key={s.id} className={marks[s.id] ? '' : 'bg-amber-50/40'} data-testid={`roster-row-${s.id}`}>
                        <td className="px-3 py-1.5 font-mono text-[12px] text-muted-foreground">{s.member_id || '—'}</td>
                        <td className="px-3 py-1.5">
                          {s.name}
                          {s.is_active === false && <span className="ml-1.5 text-[10px] uppercase text-muted-foreground">inactive</span>}
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="flex gap-1 justify-end">
                            {Object.entries(STATUS_META).map(([key, meta]) => {
                              const Icon = meta.icon;
                              const on = marks[s.id] === key;
                              return (
                                <button key={key} type="button" onClick={() => setMark(s.id, key)}
                                  title={meta.label} data-testid={`mark-${s.id}-${key}`}
                                  className={`inline-flex items-center gap-1 h-7 px-2 rounded-md border text-[11px] font-medium transition-colors ${on ? meta.on : meta.off}`}>
                                  <Icon className="w-3.5 h-3.5" weight={on ? 'fill' : 'regular'} />
                                  <span className="hidden sm:inline">{meta.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </FormSection>
            {counts.unmarked > 0 && students.length > 0 && (
              <p className="field-hint" data-testid="unmarked-hint">
                {counts.unmarked} student{counts.unmarked === 1 ? '' : 's'} still unmarked — only the ones you mark are saved.
              </p>
            )}
            {session?.session?.marked_by_name && (
              <p className="field-hint">Last saved by {session.session.marked_by_name}.</p>
            )}
          </TabsContent>

          {/* ------------------------------------------------------------- report */}
          <TabsContent value="report" className="space-y-3">
            {!report ? (
              <p className="text-[13px] text-muted-foreground">Nothing marked for this batch yet.</p>
            ) : (
              <>
                <StatTiles items={[
                  { label: 'Sessions', value: report.session_count, icon: CalendarCheck, testid: 'report-sessions' },
                  { label: 'Students', value: report.students.length, icon: Users, iconClass: 'bg-sky-100 text-sky-700' },
                  { label: 'Batch attendance', value: report.rate === null ? '—' : `${report.rate}%`, icon: ChartBar, iconClass: 'bg-violet-100 text-violet-700', testid: 'report-rate' },
                ]} />
                <FormSection title={`${report.batch.name} — attendance by student`} icon={ChartBar} bodyClassName="p-0">
                  <table className="w-full text-[13px]" data-testid="attendance-report">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="text-left font-medium px-3 py-2 w-[120px]">ID</th>
                        <th className="text-left font-medium px-3 py-2">Student</th>
                        <th className="text-right font-medium px-3 py-2">Present</th>
                        <th className="text-right font-medium px-3 py-2">Late</th>
                        <th className="text-right font-medium px-3 py-2">Absent</th>
                        <th className="text-right font-medium px-3 py-2">Excused</th>
                        <th className="text-right font-medium px-3 py-2">Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {report.students.map((r) => (
                        <tr key={r.student_id} data-testid={`report-row-${r.student_id}`}>
                          <td className="px-3 py-1.5 font-mono text-[12px] text-muted-foreground">{r.member_id || '—'}</td>
                          <td className="px-3 py-1.5">{r.name}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{r.present}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{r.late}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{r.absent}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{r.excused}</td>
                          <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${rateTone(r.rate)}`}>
                            {r.rate === null ? '—' : `${r.rate}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </FormSection>
              </>
            )}
          </TabsContent>
        </Tabs>
      </RecordPage>
    </DashboardLayout>
  );
}
